import { api } from "@/lib/api";
import type {
  CreateTagRequest,
  MediaTagKind,
  SetTagsRequest,
  TagRow,
  UpdateTagRequest,
} from "./types";

/**
 * CRUD over the global tag registry plus attach helpers for indexers
 * and media. All routes live under `/api/v1/tags`, `/api/v1/indexers/:id/tags`,
 * and `/api/v1/<media>/:id/tags` — see issue #39 / migration 0051.
 */
export const tagsApi = {
  list: () => api<TagRow[]>("/api/v1/tags"),
  create: (body: CreateTagRequest) =>
    api<TagRow>("/api/v1/tags", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  update: (id: number, body: UpdateTagRequest) =>
    api<TagRow>(`/api/v1/tags/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  remove: (id: number) =>
    api<void>(`/api/v1/tags/${id}`, { method: "DELETE" }),

  /** Tags attached to the given indexer, alphabetical. */
  forIndexer: (indexerId: number) =>
    api<TagRow[]>(`/api/v1/indexers/${indexerId}/tags`),
  /** Replace the indexer's tag set (set-semantics). */
  setForIndexer: (indexerId: number, tag_ids: number[]) =>
    api<TagRow[]>(`/api/v1/indexers/${indexerId}/tags`, {
      method: "PUT",
      body: JSON.stringify({ tag_ids } satisfies SetTagsRequest),
    }),

  /** Tags attached to the given media item. */
  forMedia: (kind: MediaTagKind, id: number) =>
    api<TagRow[]>(`/api/v1/${mediaKindPath(kind)}/${id}/tags`),
  /** Replace the media item's tag set (set-semantics). */
  setForMedia: (kind: MediaTagKind, id: number, tag_ids: number[]) =>
    api<TagRow[]>(`/api/v1/${mediaKindPath(kind)}/${id}/tags`, {
      method: "PUT",
      body: JSON.stringify({ tag_ids } satisfies SetTagsRequest),
    }),
};

/**
 * Map the wire enum value to the URL segment used by the four
 * per-media-tag routes. The backend mounts these on the canonical
 * domain prefixes (`/movies`, `/series`, `/albums`, `/artists`); we
 * deliberately don't tag at episode-level today since users overwhelmingly
 * tag whole series rather than individual episodes (the backend still
 * accepts an `episode` discriminator on the `media_tags` table for
 * future use).
 */
function mediaKindPath(kind: MediaTagKind): string {
  switch (kind) {
    case "movie":
      return "movies";
    case "series":
      return "series";
    case "album":
      return "albums";
    case "artist":
      return "artists";
    case "episode":
      // Not currently exposed via a dedicated route — tag the parent
      // series instead. This branch is here so the type checker is
      // happy without an exhaustive-enum allow.
      throw new Error(
        "episode-level tagging not exposed via HTTP — tag the parent series",
      );
  }
}
